import { useState } from "react";
import {
  RiArrowRightLine,
  RiCheckLine,
  RiDeleteBin6Line,
  RiErrorWarningLine,
  RiFileCopyLine,
  RiPencilLine,
  RiRefreshLine,
  RiStarFill,
  RiStarLine,
} from "react-icons/ri";
import { DirectionCode, PhotoSection, SECTIONS } from "./photoModel";
import { FileNode, splitFileName } from "./treeModel";
import { AngleControl, Chip } from "./TreeChips";
import TreeRowMenu, { RowMenuItem } from "./TreeRowMenu";
import TreeIndent from "./TreeIndent";

export interface TreeFileRowProps {
  node: FileNode;
  /** The name this photo will be stored under, when it differs from the dropped one. */
  derivedName?: string;
  level: number;
  posinset: number;
  setsize: number;
  selected: boolean;
  focused: boolean;
  disabled?: boolean;
  onToggleSelect(id: string): void;
  onOpen(id: string): void;
  onSetDirection(id: string, d: DirectionCode): void;
  onSetSection(id: string, s: PhotoSection): void;
  onSetMain(id: string): void;
  onDelete?(id: string): void;
  onRetry?(id: string): void;
  onFocusRow(): void;
}

export default function TreeFileRow({
  node,
  derivedName,
  level,
  posinset,
  setsize,
  selected,
  focused,
  disabled,
  onToggleSelect,
  onOpen,
  onSetDirection,
  onSetSection,
  onSetMain,
  onDelete,
  onRetry,
  onFocusRow,
}: TreeFileRowProps) {
  const [preview, setPreview] = useState(false);
  const { draft, conflict, upload } = node;
  const original = draft.origin.fileName;
  const { prefix, tail } = splitFileName(original, draft.barcode, draft.matCode);
  const renamed = derivedName && derivedName !== original ? derivedName : undefined;
  const otherSection: PhotoSection = draft.section === "cover" ? "packshot" : "cover";

  const items: RowMenuItem[] = [
    {
      label: draft.isMain ? "เป็นรูปหลักอยู่แล้ว" : "ตั้งเป็นรูปหลัก",
      icon: draft.isMain ? <RiStarFill size={14} /> : <RiStarLine size={14} />,
      onSelect: () => onSetMain(draft.id),
      disabled: disabled || draft.isMain,
    },
    {
      label: `ย้ายไป ${otherSection}/`,
      icon: <RiArrowRightLine size={14} />,
      onSelect: () => onSetSection(draft.id, otherSection),
      disabled: disabled || !SECTIONS.includes(otherSection),
    },
    {
      label: "แก้รายละเอียด",
      icon: <RiPencilLine size={14} />,
      onSelect: () => onOpen(draft.id),
    },
    {
      label: "ลบรูปนี้",
      icon: <RiDeleteBin6Line size={14} />,
      onSelect: () => onDelete?.(draft.id),
      danger: true,
      disabled: disabled || !onDelete,
    },
  ];

  return (
    <div
      role="treeitem"
      data-row-id={draft.id}
      aria-level={level}
      aria-posinset={posinset}
      aria-setsize={setsize}
      aria-selected={selected}
      aria-label={original}
      tabIndex={focused ? 0 : -1}
      onFocus={onFocusRow}
      className={`group relative flex items-stretch transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${
        selected ? "bg-blue-50" : "hover:bg-gray-50"
      }`}
    >
      <TreeIndent level={level} />

      <div className="flex min-w-0 flex-1 items-center gap-2 py-1 pr-1">
        <span className="-m-2 flex shrink-0 p-2">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(draft.id)}
            tabIndex={focused ? 0 : -1}
            aria-label={`เลือก ${original}`}
            className="h-3.5 w-3.5 cursor-pointer accent-blue-600"
          />
        </span>

        {/* Thumbnail — the photo is the evidence, so it stays on the row and grows on demand. */}
        <span className="relative shrink-0">
          <button
            type="button"
            onClick={() => onOpen(draft.id)}
            onMouseEnter={() => setPreview(true)}
            onMouseLeave={() => setPreview(false)}
            onFocus={() => setPreview(true)}
            onBlur={() => setPreview(false)}
            tabIndex={focused ? 0 : -1}
            aria-label={`ดู ${original}`}
            className={`block h-8 w-8 overflow-hidden rounded border bg-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${
              draft.isMain ? "border-blue-600" : conflict ? "border-orange-300" : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <img src={draft.previewUrl} alt="" className="h-full w-full object-contain" />
          </button>
          {/* Desktop-only: a 160px panel has nowhere to go beside a phone-width row,
              where tapping the thumbnail opens the full editor instead. */}
          {preview && (
            <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 hidden w-[10.5rem] -translate-y-1/2 rounded-lg border border-gray-200 bg-white p-1 shadow-[0_10px_15px_-3px_rgba(0,0,0,.1),0_4px_6px_-4px_rgba(0,0,0,.1)] lg:block">
              {/* The width above is load-bearing: anchored at `left-full` inside a 32px
                  cell this box would shrink-to-fit to nothing, and Tailwind's preflight
                  `img{max-width:100%}` would then clamp the photo to zero width. */}
              <img src={draft.previewUrl} alt="" className="h-40 w-full object-contain" />
            </span>
          )}
          {draft.isMain && (
            <RiStarFill
              size={11}
              className="absolute -bottom-0.5 -right-0.5 text-blue-600"
              aria-label="รูปหลัก"
            />
          )}
        </span>

        {/* Filename — the shared prefix recedes so the part that tells siblings apart leads.
            Below `sm` the prefix is dropped altogether rather than truncated: every sibling
            carries the same one, so it is pure cost, and ellipsising from the right would
            eat the tail and leave three identical-looking rows. */}
        <span className="min-w-0 flex-1 truncate text-xs leading-5">
          {prefix && (
            <>
              <span className="text-gray-400 sm:hidden" aria-hidden="true">…</span>
              <span className="hidden text-gray-400 tabular-nums sm:inline">{prefix}</span>
            </>
          )}
          <span className="font-semibold text-gray-800 tabular-nums">{tail}</span>
          {renamed && (
            <span className="ml-2 hidden text-gray-500 md:inline" title={`จะถูกเก็บเป็น ${renamed}`}>
              → {renamed}
            </span>
          )}
        </span>

        {conflict && (
          <Chip tone="dup" icon={<RiFileCopyLine size={13} aria-hidden="true" />}>
            <span className="sr-only sm:not-sr-only">ซ้ำของเดิม</span>
          </Chip>
        )}
        {upload?.status === "success" && (
          <Chip tone="ready" icon={<RiCheckLine size={13} aria-hidden="true" />}>
            <span className="sr-only sm:not-sr-only">อัปโหลดแล้ว</span>
          </Chip>
        )}
        {upload?.status === "failed" && (
          <Chip tone="error" icon={<RiErrorWarningLine size={13} aria-hidden="true" />}>
            <span className="sr-only sm:not-sr-only">ล้มเหลว</span>
          </Chip>
        )}
        {upload?.status === "failed" && onRetry && (
          <button
            type="button"
            onClick={() => onRetry(draft.id)}
            tabIndex={focused ? 0 : -1}
            aria-label={`ลองใหม่ ${original}`}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-red-500 transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
          >
            <RiRefreshLine size={14} aria-hidden="true" />
          </button>
        )}

        <AngleControl
          direction={draft.direction}
          onSetDirection={(d) => onSetDirection(draft.id, d)}
          fileLabel={original}
          disabled={disabled}
          tabIndex={focused ? 0 : -1}
        />

        <TreeRowMenu items={items} label={`ตัวเลือกของ ${original}`} tabIndex={focused ? 0 : -1} />
      </div>

      {upload?.status === "uploading" && (
        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-blue-100" aria-hidden="true">
          <span
            className="block h-full bg-blue-600 transition-[width] duration-300"
            style={{ width: `${upload.progress}%` }}
          />
        </span>
      )}
    </div>
  );
}
